import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Layout (always present — kept in the main bundle)
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';

// Route guards & shared UI (tiny, used inline across routes — kept eager)
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminRoute from './components/Auth/AdminRoute';
import LoadingSpinner from './components/UI/LoadingSpinner';
import { initFontOptimization } from './utils/fontOptimization';
import { initPerformanceOptimizations } from './utils/performanceOptimization';

// Pages — lazy-loaded so each route ships as its own chunk instead of
// bloating the initial bundle every visitor downloads.
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const Categories = lazy(() => import('./pages/Categories'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Login = lazy(() => import('./pages/Auth/Login'));
const Register = lazy(() => import('./pages/Auth/Register'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const MyAccount = lazy(() => import('./pages/MyAccount'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentFailure = lazy(() => import('./pages/PaymentFailure'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));

// Info Pages
const AboutUs = lazy(() => import('./pages/Info/AboutUs'));
const Contact = lazy(() => import('./pages/Info/Contact'));
const FAQ = lazy(() => import('./pages/Info/FAQ'));
const ShippingPolicy = lazy(() => import('./pages/Info/ShippingPolicy'));
const ReturnsExchangePolicy = lazy(() => import('./pages/Info/ReturnsExchangePolicy'));
const PrivacyPolicy = lazy(() => import('./pages/Info/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/Info/TermsOfService'));

// Service Pages
const ThreeDPrinting = lazy(() => import('./pages/Services/ThreeDPrinting'));
const ThreeDDesignPrototyping = lazy(() => import('./pages/Services/ThreeDDesignPrototyping'));

// Quote Pages
const MyQuotes = lazy(() => import('./pages/MyQuotes'));

// Admin Pages — the entire admin panel is split out so storefront
// visitors never download it.
const AdminDashboard = lazy(() => import('./pages/Admin/Dashboard'));
const AdminProducts = lazy(() => import('./pages/Admin/Products'));
const AdminOrders = lazy(() => import('./pages/Admin/Orders'));
const AdminUsers = lazy(() => import('./pages/Admin/Users'));
const AdminCategories = lazy(() => import('./pages/Admin/Categories'));
const AdminCoupons = lazy(() => import('./pages/Admin/Coupons'));
const AdminShipping = lazy(() => import('./pages/Admin/Shipping'));
const AdminMessages = lazy(() => import('./pages/Admin/Messages'));
const AdminAvailableColors = lazy(() => import('./pages/Admin/AvailableColors'));
const AdminQuotes = lazy(() => import('./components/Admin/AdminQuotes'));
const AdminLayout = lazy(() => import('./components/Layout/AdminLayout'));
const AdminSetup = lazy(() => import('./components/AdminSetup'));

function App({ ssrData }) {
  const { loading } = useAuth();

  // Initialize font optimization on app load
  useEffect(() => {
    initFontOptimization();
    initPerformanceOptimizations();
  }, []);

  // Store SSR data for components that need it
  useEffect(() => {
    if (ssrData) {
      // Make SSR data available to components
      window.__APP_SSR_DATA__ = ssrData;
    }
  }, [ssrData]);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="App">
      <Navbar />
      <main style={{ minHeight: 'calc(100vh - 140px)' }}>
        <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          {/* Informational Pages */}
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/returns" element={<ReturnsExchangePolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* Service Pages */}
          <Route path="/services/3d-printing" element={<ThreeDPrinting />} />
          <Route path="/services/3d-design-prototyping" element={<ThreeDDesignPrototyping />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          
          {/* Development Routes */}
          <Route path="/admin-setup" element={
            <ProtectedRoute>
              <AdminSetup />
            </ProtectedRoute>
          } />
          
          {/* Protected Routes */}
          <Route path="/checkout" element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <MyAccount />
            </ProtectedRoute>
          } />
          <Route path="/orders" element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          } />
          <Route path="/orders/:id" element={
            <ProtectedRoute>
              <OrderDetail />
            </ProtectedRoute>
          } />
          <Route path="/my-quotes" element={
            <ProtectedRoute>
              <MyQuotes />
            </ProtectedRoute>
          } />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-failure" element={<PaymentFailure />} />
          <Route path="/order-confirmation/:id" element={
            <ProtectedRoute>
              <OrderConfirmation />
            </ProtectedRoute>
          } />
          
          {/* Admin Routes (nested for persistent layout) */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="quotes" element={<AdminQuotes />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="colors" element={<AdminAvailableColors />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="shipping" element={<AdminShipping />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="settings" element={<div>Settings coming soon...</div>} />
          </Route>
        </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

export default App;
